import React, { useRef, useState } from "react";
import Modal from "react-modal";
import ProductListPageTypeConfig from "../ProductListPageTypeConfig";
import { Currency } from "../../../../js/common/CurrencyFormatter";
import applyFilterButtonService from "./ApplyFilterButtonService";
import ProductListSellTagsFilter from "./ProductListSellTagsFilter";
import { FilterSourceData, FilterValues } from "./FilterValuesCollection";
import ProductListBookAuthorFilter from "./ProductListBookAuthorFilter";
import ProductListPackageProducerFilter from "./ProductListPackageProducerFilter";
import ProductListSubcategoriesFilter from "./ProductListSubcategoriesFilter";
import ProductListProductStatusFilter from "./ProductListProductStatusFilter";
import ProductListParametersFilter from "./ProductListParametersFilter";
import ProductListPriceFilter from "./ProductListPriceFilter";
import ProductListInStockFilter from "./ProductListInStockFilter";

interface ProductListFiltersModalProperties {
    readonly pageTypeConfig: ProductListPageTypeConfig;
    readonly filterData: FilterSourceData;
    readonly currency: Currency;
    readonly initialFilter: FilterValues;

    readonly onAfterClose: () => void;
}

const isMobileVersion = window.innerWidth < 768;

export default function ProductListFiltersModal({ pageTypeConfig, filterData, currency, initialFilter, onAfterClose }: ProductListFiltersModalProperties): React.JSX.Element {
    const applyFilterButtonFixedContainer = useRef<HTMLDivElement>(null);
    const fixedApplyFilterButton = useRef<HTMLButtonElement>(null);

    const [modalIsOpen, setModalIsOpen] = useState(true);
    const [filter, setFilter] = useState<FilterValues>(initialFilter);
    const [activeFilterTabs, setActiveFilterTabs] = useState<Set<string>>(new Set(["sellTags"]));

    const setActiveFilterTab = (tabName: string): void => {
        if (isMobileVersion) {
            const newActiveFilterTabs = new Set(activeFilterTabs);

            if (activeFilterTabs.has(tabName)) {
                newActiveFilterTabs.delete(tabName);
            } else {
                newActiveFilterTabs.add(tabName);
            }

            setActiveFilterTabs(newActiveFilterTabs);

            return;
        }

        setActiveFilterTabs(new Set([tabName]));
    };

    const closeModal = (): void => {
        setModalIsOpen(false);
    };

    const handleApplyFilters = (filterValues: FilterValues): void => {
        setModalIsOpen(false);
        applyFilterButtonService.applySelectedFilters(filterValues);
    };

    return (
        <Modal
            isOpen={modalIsOpen}
            onRequestClose={closeModal}
            onAfterClose={onAfterClose}
            className="modal-dialog modal-transition"
            overlayClassName="react-modal-backdrop modal-transition shop-modal-change"
        >
            <div className="modal-content filters">
                <div className="modal-header empty">
                    <button type="button" className="close" onClick={closeModal}>
                        <i className="fa-solid fa-times"></i>
                    </button>
                </div>

                <div className="modal-body action-price-modal-content">
                    <div className="well well-transparent">
                        <p className="fakeh3">Nastavit filtry</p>

                        <div className="set-filter-button-container" ref={applyFilterButtonFixedContainer}>
                            <div>
                                <button className="btn btn-success apply-filters" type="button" ref={fixedApplyFilterButton} onClick={() => handleApplyFilters(filter)}><i
                                    className="fa-solid fa-circle-check">
                                </i>Aktivuj filtry
                                </button>
                            </div>
                        </div>

                        <div id="asideFilterContainer">
                            <ProductListSellTagsFilter
                                isActive={activeFilterTabs.has("sellTags")}
                                sellTags={filterData.sellTags}
                                filter={filter}
                                isSoldProductsPage={pageTypeConfig.isSoldProductsPage}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("sellTags")}
                            />
                            <ProductListSubcategoriesFilter
                                isActive={activeFilterTabs.has("subcategories")}
                                subcategories={filterData.subcategories}
                                filter={filter}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("subcategories")} />
                            {pageTypeConfig.isPreferredBookAuthorFilter
                                ? <ProductListBookAuthorFilter
                                    isActive={activeFilterTabs.has("bookAuthors")}
                                    bookAuthors={filterData.bookAuthors}
                                    filter={filter}
                                    onFilterChange={setFilter}
                                    onActivateTab={() => setActiveFilterTab("bookAuthors")}
                                />
                                : <ProductListPackageProducerFilter
                                    isActive={activeFilterTabs.has("packageProducers")}
                                    packageProducers={filterData.packageProducers}
                                    filter={filter}
                                    onFilterChange={setFilter}
                                    onActivateTab={() => setActiveFilterTab("packageProducers")}
                                />}
                            <ProductListProductStatusFilter
                                isActive={activeFilterTabs.has("productStatuses")}
                                productStatuses={filterData.productStatuses}
                                filter={filter}
                                isSoldProductsPage={pageTypeConfig.isSoldProductsPage}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("productStatuses")} />
                            <ProductListParametersFilter
                                isActive={activeFilterTabs.has("parameters")}
                                parameters={filterData.parameters}
                                filter={filter}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("parameters")} />
                            <ProductListPriceFilter
                                isActive={activeFilterTabs.has("price")}
                                minPrice={filterData.minPrice}
                                maxPrice={filterData.maxPrice}
                                currency={currency}
                                filter={filter}
                                isSoldProductsPage={pageTypeConfig.isSoldProductsPage}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("price")} />
                            <ProductListInStockFilter
                                isActive={activeFilterTabs.has("inStock")}
                                filter={filter}
                                onFilterChange={setFilter}
                                onActivateTab={() => setActiveFilterTab("inStock")} />
                        </div>

                        <div className="clearfix"></div>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
